import SideNav from './sections/SideNav';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { useRefreshCurrentUser } from '~/api';
import { TopNav } from './sections/TopNav';
import { useEffect, useState } from 'react';
import { navItems } from './config';
import { ThemedLayout } from './ThemedLayout';
import Intercom from '@intercom/messenger-js-sdk';

export function AuthLayout() {
  const { isLoading, isError } = useRefreshCurrentUser();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (isLoading) return;
    if (!role) return;
    const currentNavItem = navItems.find(
      (item) => item.to === location.pathname
    );
    if (!currentNavItem) return;

    if (user?.id) {
      Intercom({
        app_id: 'jbh67q8f',
        user_id: user.id,
        name: user.externalId,
        email: user.externalId,
      });
    }

    const hasRequiredRole = currentNavItem.requiredRoles.some(
      (requiredRole) => requiredRole.toLowerCase() === role.toLowerCase()
    );

    if (!hasRequiredRole) navigate('/');
  }, [isLoading, role, location.pathname, navigate, user]);

  if (isLoading) return <div>Loading your account…</div>;
  if (isError) return <div>Could not load your profile.</div>;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <ThemedLayout>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-neut-prim/50 z-40  lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={`w-64 bg-neut-bg-alt pt-6 border-r border-neut-bg shadow-sm 
                      h-screen fixed lg:static z-50 transition-transform duration-300
                      lg:translate-x-0 ${
                        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                      }`}
      >
        <SideNav onNavigate={() => setIsSidebarOpen(false)} />
      </div>
      <div className="w-full lg:w-[calc(100%-256px)] lg:ml-0 min-h-[100vh] max-h-[100vh] overflow-y-auto bg-neut-bg">
        <TopNav onToggleSidebar={toggleSidebar} />
        <div className="w-full p-3 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </ThemedLayout>
  );
}
